"use client";

import { Loader2 } from "lucide-react";

/**
 * StickyActionBar — bottom-of-form action row with a primary CTA + secondary
 * actions. Sticky on scroll so it's always reachable.
 *
 * Primary button uses --cc-accent-* gradient — themes to user's accent color.
 *
 * Use `secondary` for save/clear/cancel etc. They render as ghost buttons
 * to the right of the primary CTA.
 */
export interface StickyActionBarProps {
  primaryLabel: React.ReactNode;
  primaryIcon?: React.ReactNode;
  loadingLabel?: React.ReactNode;
  loading?: boolean;
  disabled?: boolean;
  onPrimary: () => void;
  secondary?: React.ReactNode;
  className?: string;
}

export function StickyActionBar({
  primaryLabel,
  primaryIcon,
  loadingLabel,
  loading,
  disabled,
  onPrimary,
  secondary,
  className = "",
}: StickyActionBarProps) {
  return (
    <div
      className={`border p-3 flex flex-wrap items-center gap-2 sticky bottom-2 backdrop-blur-sm ${className}`}
      style={{
        borderRadius: "var(--gam-radius-card, 0.75rem)",
        borderColor: "var(--cc-accent-500-50)",
        background:
          "linear-gradient(to left, var(--cc-accent-500-15), rgba(30, 41, 59, 0.5), rgba(30, 41, 59, 0.4))",
        boxShadow: "var(--gam-shadow-card, none)",
      }}
    >
      <button
        type="button"
        onClick={onPrimary}
        disabled={disabled || loading}
        className="flex-1 min-w-[200px] flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-semibold text-white shadow-lg disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none transition cursor-pointer"
        style={{
          borderRadius: "var(--gam-radius-btn, 0.5rem)",
          background:
            "linear-gradient(to left, var(--cc-accent-600), var(--cc-accent-500))",
          boxShadow: "0 10px 15px -3px var(--cc-accent-600-30)",
          transitionTimingFunction: "var(--gam-spring-default, cubic-bezier(0.4, 0, 0.2, 1))",
        }}
      >
        {loading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          primaryIcon
        )}
        {loading ? loadingLabel ?? primaryLabel : primaryLabel}
      </button>
      {secondary}
    </div>
  );
}
